import { Component } from 'react';
import PropTypes from 'prop-types';
import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from '@/utils/lazyWithRetry';
import './ErrorBoundary.css';

/**
 * Catches render errors so a single crashing component no longer
 * unmounts the whole app and leaves a blank white page.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error(
      'ErrorBoundary caught an error:',
      error,
      info?.componentStack
    );

    if (isChunkLoadError(error)) {
      reloadOnceForChunkError();
    }
  }

  componentDidUpdate(prevProps) {
    // Recover automatically when the user navigates to another route
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="error-boundary">
        <h4 className="error-boundary__title">Something went wrong</h4>
        <p className="error-boundary__text">
          The page stopped responding. Please reload to continue.
        </p>
        <button
          type="button"
          className="btn btn-primary error-boundary__button"
          onClick={this.handleReload}
        >
          Reload page
        </button>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  resetKey: PropTypes.string,
};

export default ErrorBoundary;
